import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import * as stylex from "@stylexjs/stylex";
import { z } from "zod";
import Button from "@/components/ui/Button";
import CenteredCard from "@/components/ui/CenteredCard";
import { recipes } from "@/design/recipes";
import "@/design/tokens.stylex";
import { colors, radii, space, text } from "@/design/tokens.stylex";
import { beginGoogleSignIn, useSession } from "@/lib/auth-client";
import { useDocumentTitle } from "@/lib/window-title";

// `next` is an app-relative path to return to after sign-in (set by the
// authenticated layout on an expired session and by the OAuth consent page).
// Anything that is not a plain in-app path is ignored; the fallback `/` lands
// on the person's Last place.
const loginSearchSchema = z.object({
  next: z.string().optional().catch(undefined)
});
function safeNext(next: string | undefined): string {
  if (!next || !next.startsWith("/") || next.includes("\\")) return "/";
  try {
    const url = new URL(next, window.location.origin);
    if (url.origin !== window.location.origin) return "/";
    return `${url.pathname}${url.search}`;
  } catch {
    return "/";
  }
}
const styles = {
  card: {
    kOIVth: "x1dfszw7",
    $$css: true
  },
  logoLink: {
    k1xSpc: "x3nfvp2",
    kSGwAc: "xqcrz7y",
    kaIpWk: "x1wpy2cq",
    $$css: true
  },
  full: {
    kzqmXN: "xh8yej3",
    $$css: true
  },
  footLink: {
    kMwMTN: "x1i6qc9y xfpavck",
    kybGjl: "x1bvjpef",
    kcSHmL: "xda060z",
    kaIpWk: "x1wpy2cq",
    $$css: true
  }
};
export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  component: LoginPage
});
function LoginPage() {
  const {
    next
  } = Route.useSearch();
  const target = safeNext(next);
  useDocumentTitle({
    section: "Sign in"
  });
  const {
    data: session,
    isPending
  } = useSession();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isPending || !session) return;
    if (target === "/") void navigate({
      to: "/",
      replace: true
    });else window.location.replace(target);
  }, [isPending, session, navigate, target]);
  async function handleGoogle() {
    setError(null);
    setLoading(true);
    try {
      await beginGoogleSignIn(target);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Sign-in failed");
      setLoading(false);
    }
  }
  return <CenteredCard width="sm" xstyle={styles.card} below={<p className="x1ghz6dp x7x6yu3 x1i6qc9y">
          <Link to="/personvern" {...stylex.props(styles.footLink, recipes.focusRing)}>
            Privacy
          </Link>
          {" · "}
          <a href="mailto:marketing@askeladden.co" {...stylex.props(styles.footLink, recipes.focusRing)}>
            Contact
          </a>
        </p>}>
      <a href="/" {...stylex.props(styles.logoLink, recipes.focusRing)}>
        <img src="/logo.svg" alt="A&amp;CO" className="x1lliihq xxk0z11 x14atkfc" />
      </a>
      <div className="x78zum5 xdt5ytf x166cprz">
        <h1 className="x1ghz6dp xuxpmqx xk50ysn xgwkh9f">Sign in</h1>
        <p className="x1ghz6dp xtu3avg x1evy7pa x1e3avs9">
          Access is granted by an administrator. If you can't sign in, ask them to add your email
          address.
        </p>
      </div>
      <Button variant="primary" onClick={handleGoogle} disabled={loading} icon={<GoogleMark />} xstyle={styles.full}>
        {loading ? "Redirecting…" : "Continue with Google"}
      </Button>
      {error && <p role="alert" className="x1ghz6dp xtu3avg x1nif09i x2b8uid">
          {error}
        </p>}
    </CenteredCard>;
}
function GoogleMark() {
  return <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>;
}